// ── V156: 다 한 작업은 내역에 저장, 안 한 작업은 다음 날 다시 ─────────────────────────
// 사장님: "미완료된 작업이 남아 있으면 다음 날 다시 띄워지게 해주고, 완료된 작업은 저장하게 해줘"
//   · 날이 바뀌면(목록 날짜 < 오늘) 자동으로: 체크한 작업 → 내역(체크한 날짜별), 안 한 작업 → 오늘 목록에 그대로
//   · 💾 완료 저장 버튼도 같은 일 (날짜는 그대로, 다 한 것만 내역으로)
//   · 내역은 날짜마다 한 건 (id 'day-YYYY-MM-DD'). 같은 날 여러 번 저장하면 그 건에 합친다
//     → 여러 기기가 동시에 넘겨도 결과가 같다 (항목 id로 중복 없이 합침)
import { todayKey } from './workCalc';

export const DAY_ENTRY = (date) => `day-${date}`;
export const WORK_HISTORY_MAX = 120;

/** 날이 바뀌어서 넘겨야 하나 */
export function needsRollover(workToday, today) {
  const items = Array.isArray(workToday?.items) ? workToday.items : [];
  const d = String(workToday?.date || '');
  return items.length > 0 && !!d && d < today;
}

/**
 * 다 한 작업을 내역으로 옮긴다
 * @param o.workToday { date, items } · o.workChecks { id: { done, at, by } } · o.workHistory []
 * @param o.today 'YYYY-MM-DD' · o.memo 메모(그날 내역에 붙임) · o.who 저장한 사람 · o.auto 날이 바뀌어 자동으로 넘기는 중
 * @returns null(할 일 없음) | { workToday, workChecks, workHistory, saved, carried }
 */
export function archiveDone({ workToday, workChecks = {}, workHistory = [], today, memo = '', who = '', auto = false, now = Date.now() } = {}) {
  const items = Array.isArray(workToday?.items) ? workToday.items : [];
  const listDate = String(workToday?.date || '') || today;
  const isDone = (i) => !!(workChecks && workChecks[i.id] && workChecks[i.id].done);
  const done = items.filter(isDone);
  const left = items.filter(i => !isDone(i));
  const rolling = auto && needsRollover(workToday, today);
  if (!done.length && !rolling && !String(memo || '').trim()) return null;

  // 다 한 작업 — 체크한 날짜별로 묶는다 (체크 시각이 없으면 목록 날짜)
  const groups = new Map();
  for (const i of done) {
    const c = workChecks[i.id] || {};
    let d = c.at ? todayKey(new Date(c.at)) : listDate;
    if (!d || d > today) d = today;
    const snap = { ...i, done: true, ...(c.at ? { at: c.at } : {}), ...(c.by ? { by: c.by } : {}) };
    if (!groups.has(d)) groups.set(d, []);
    groups.get(d).push(snap);
  }
  const m = String(memo || '').trim().slice(0, 500);
  if (m && !groups.has(listDate > today ? today : listDate)) groups.set(listDate > today ? today : listDate, []);

  let hist = Array.isArray(workHistory) ? [...workHistory] : [];
  for (const [date, list] of groups) {
    const id = DAY_ENTRY(date);
    const prev = hist.find(h => h && h.id === id);
    const byId = new Map(((prev && prev.items) || []).map(x => [x.id, x]));
    for (const x of list) byId.set(x.id, x);
    const merged = [...byId.values()];
    const memoOut = [prev && prev.memo, date === (listDate > today ? today : listDate) ? m : ''].filter(Boolean)
      .filter((v, k, a) => a.indexOf(v) === k).join('\n').slice(0, 1000);
    const entry = {
      id, date, items: merged,
      total: merged.length, doneCount: merged.filter(x => x.done).length,
      savedAt: now, savedBy: String(who || (prev && prev.savedBy) || '').slice(0, 30),
      daily: true,
      ...(memoOut ? { memo: memoOut } : {}),
    };
    hist = [entry, ...hist.filter(h => !h || h.id !== id)];
  }
  // 최신 날짜가 위로 (같은 날은 나중 저장이 위)
  hist.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0));
  hist = hist.slice(0, WORK_HISTORY_MAX);

  return {
    workToday: { date: rolling ? today : (left.length ? listDate : today), items: left },
    workChecks: {},   // 빈 맵 = 칸 전체를 바꾼다 (남은 작업은 아직 체크 전)
    workHistory: hist,
    saved: done.length,
    carried: left.length,
  };
}
